import { TaskTemplate, WorkflowDefFormData, TaskDefFormData, ScheduleFormData } from './types';

// ============================================================================
// TASK TYPES
// ============================================================================

export const TASK_TYPES = {
  SCRIPT_EXECUTION: 'SCRIPT_EXECUTION',
  REST_CONNECTOR: 'REST_CONNECTOR',
  USER_INPUT: 'USER_INPUT',
  HAYSTACK: 'HAYSTACK',
  SWITCH: 'SWITCH',
  FORK_JOIN: 'FORK_JOIN',
  DO_WHILE: 'DO_WHILE',
} as const;

// ============================================================================
// TASK TEMPLATES
// ============================================================================

export const TASK_TEMPLATES: TaskTemplate[] = [
  {
    name: 'Script Execution',
    type: TASK_TYPES.SCRIPT_EXECUTION,
    description: 'Execute custom JavaScript logic within the workflow for lightweight computations and data transformations',
    icon: 'fa-code',
    color: '#10b981',
    category: 'Data',
    defaultConfig: {
      _type: TASK_TYPES.SCRIPT_EXECUTION,
      _retryCount: 3,
      _retryLogic: 'FIXED',
      _retryDelaySeconds: 60,
      _timeoutSeconds: 300,
      _inputParams: {
        _userType: '',
        _scriptName: '',
      },
    },
    documentation: `
**Script Execution Task**

Runs custom JavaScript logic directly within the workflow. Ideal for:
- Data transformations
- Conditional logic
- Lightweight computations

**Required Parameters:**
- \`_userType\`: The script's user type
- \`_scriptName\`: Name of the script to execute

**Example:**
\`\`\`json
{
  "_type": "SCRIPT_EXECUTION",
  "_name": "transform_data",
  "_inputParams": {
    "_userType": "data_transformer",
    "_scriptName": "transformData"
  }
}
\`\`\`
    `.trim(),
  },
  {
    name: 'REST Connector',
    type: TASK_TYPES.REST_CONNECTOR,
    description: 'Make HTTP requests to external APIs with support for multiple authentication methods',
    icon: 'fa-exchange-alt',
    color: '#3b82f6',
    category: 'Integration',
    defaultConfig: {
      _type: TASK_TYPES.REST_CONNECTOR,
      _retryCount: 3,
      _retryLogic: 'EXPONENTIAL_BACKOFF',
      _retryDelaySeconds: 60,
      _timeoutSeconds: 30,
      _inputParams: {
        _url: '',
        _method: 'GET',
        _headers: {},
        _body: {},
        _auth: {
          _type: 'NoAuth',
        },
      },
    },
    documentation: `
**REST Connector Task**

Makes HTTP requests to external services. Supports:
- Multiple HTTP methods (GET, POST, PUT, DELETE, PATCH)
- Various authentication schemes (Basic, Bearer, OAuth2)
- Custom headers and request bodies

**Authentication Types:**
- \`NoAuth\`: No authentication
- \`BasicAuth\`: Username/password
- \`BearerToken\`: Token-based auth
- \`OAuth2ClientCredentials\`: OAuth2 client credentials flow
- \`OAuth2Password\`: OAuth2 password grant
- \`OAuth2ClientCredentialsJson\`: OAuth2 with JSON body

**Example:**
\`\`\`json
{
  "_type": "REST_CONNECTOR",
  "_name": "fetch_user_data",
  "_inputParams": {
    "_url": "https://api.example.com/users",
    "_method": "GET",
    "_auth": {
      "_type": "BearerToken",
      "_params": {
        "_token": "your-token-here"
      }
    }
  }
}
\`\`\`
    `.trim(),
  },
  {
    name: 'User Input',
    type: TASK_TYPES.USER_INPUT,
    description: 'Pause workflow execution until a user provides required input or approval',
    icon: 'fa-user-edit',
    color: '#f59e0b',
    category: 'User Interaction',
    defaultConfig: {
      _type: TASK_TYPES.USER_INPUT,
      _timeoutSeconds: 86400, // 24 hours
      _timeoutPolicy: 'TIME_OUT_WF',
      _inputParams: {
        _sendNotification: 'yes',
        _notificationId: '',
        _userInputPermissions: [],
        _userInput: {},
      },
    },
    documentation: `
**User Input Task**

Pauses workflow until user provides input. Perfect for:
- Approval workflows
- Manual data entry
- Decision points

**Required Parameters:**
- \`_sendNotification\`: "yes" or "no"
- \`_notificationId\`: Unique notification identifier
- \`_userInputPermissions\`: Array of users/groups who can respond
- \`_userInput\`: Schema defining expected input fields

**Example:**
\`\`\`json
{
  "_type": "USER_INPUT",
  "_name": "approval_required",
  "_inputParams": {
    "_sendNotification": "yes",
    "_notificationId": "approval-001",
    "_userInputPermissions": [{
      "_userType": "user",
      "_id": "user-id"
    }],
    "_userInput": {
      "decision": {
        "type": "string",
        "enum": ["approved", "rejected"]
      }
    }
  }
}
\`\`\`
    `.trim(),
  },
  {
    name: 'Haystack',
    type: TASK_TYPES.HAYSTACK,
    description: 'Connect to Project Haystack servers for building automation and IoT data',
    icon: 'fa-database',
    color: '#8b5cf6',
    category: 'Integration',
    defaultConfig: {
      _type: TASK_TYPES.HAYSTACK,
      _retryCount: 3,
      _retryLogic: 'FIXED',
      _retryDelaySeconds: 60,
      _timeoutSeconds: 60,
      _inputParams: {
        _project: '',
        _url: '',
        _uname: '',
        _pwd: '',
        _action: 'pointhistory',
        _cmd: '',
      },
    },
    documentation: `
**Haystack Task**

Connects to Project Haystack servers for IoT/building automation data.

**Required Parameters:**
- \`_project\`: Haystack project name
- \`_url\`: Haystack endpoint URL
- \`_uname\`: Username
- \`_pwd\`: Password
- \`_action\`: Action to perform (e.g., "pointhistory")
- \`_cmd\`: Query command

**Example:**
\`\`\`json
{
  "_type": "HAYSTACK",
  "_name": "fetch_sensor_data",
  "_inputParams": {
    "_project": "building_a",
    "_url": "https://haystack.example.com/api",
    "_uname": "user",
    "_pwd": "password",
    "_action": "pointhistory",
    "_cmd": "point and his and hisStatus == \\"ok\\"",
    "_hisStartDate": "2025-01-01"
  }
}
\`\`\`
    `.trim(),
  },
  {
    name: 'Switch',
    type: TASK_TYPES.SWITCH,
    description: 'Route workflow execution based on conditional expressions (like if/else)',
    icon: 'fa-code-branch',
    color: '#ec4899',
    category: 'Control Flow',
    defaultConfig: {
      _type: TASK_TYPES.SWITCH,
      _switchCaseValue: '',
      _decisionCases: {},
      _defaultCase: [],
    },
    documentation: `
**Switch Task**

Routes workflow based on a condition. Like if/else statements.

**Required Parameters:**
- \`_switchCaseValue\`: Expression to evaluate (supports \${...} syntax)
- \`_decisionCases\`: Object mapping case values to task arrays
- \`_defaultCase\`: Tasks to execute if no case matches

**Example:**
\`\`\`json
{
  "_type": "SWITCH",
  "_name": "route_by_status",
  "_switchCaseValue": "\${user_input._output._userInput.decision}",
  "_decisionCases": {
    "approved": [
      { "_type": "SCRIPT_EXECUTION", "_name": "process_approval" }
    ],
    "rejected": [
      { "_type": "SCRIPT_EXECUTION", "_name": "handle_rejection" }
    ]
  },
  "_defaultCase": [
    { "_type": "SCRIPT_EXECUTION", "_name": "handle_unknown" }
  ]
}
\`\`\`

**Integration Pattern: Content-Based Router**
Use Switch tasks to route messages/data to different processing paths based on content.
    `.trim(),
  },
  {
    name: 'Fork Join',
    type: TASK_TYPES.FORK_JOIN,
    description: 'Execute multiple task branches in parallel for improved performance',
    icon: 'fa-project-diagram',
    color: '#06b6d4',
    category: 'Control Flow',
    defaultConfig: {
      _type: TASK_TYPES.FORK_JOIN,
      _forkTasks: [[], []],
    },
    documentation: `
**Fork Join Task**

Executes multiple task branches in parallel. Great for:
- Parallel API calls
- Independent data processing
- Performance optimization

**Important:** Cannot nest Fork Join tasks within each other.

**Required Parameters:**
- \`_forkTasks\`: Array of task arrays to run in parallel

**Example:**
\`\`\`json
{
  "_type": "FORK_JOIN",
  "_name": "parallel_processing",
  "_forkTasks": [
    [
      { "_type": "REST_CONNECTOR", "_name": "fetch_user" },
      { "_type": "SCRIPT_EXECUTION", "_name": "process_user" }
    ],
    [
      { "_type": "REST_CONNECTOR", "_name": "fetch_orders" },
      { "_type": "SCRIPT_EXECUTION", "_name": "process_orders" }
    ]
  ]
}
\`\`\`

**Integration Pattern: Scatter-Gather**
1. Fork to query multiple services in parallel
2. Each branch processes its response
3. Join results for aggregation
    `.trim(),
  },
  {
    name: 'Do While',
    type: TASK_TYPES.DO_WHILE,
    description: 'Repeatedly execute tasks until a condition is met (loop)',
    icon: 'fa-sync',
    color: '#f97316',
    category: 'Control Flow',
    defaultConfig: {
      _type: TASK_TYPES.DO_WHILE,
      _loopCondition: '',
      _loopOver: [],
    },
    documentation: `
**Do While Task**

Repeats tasks until a condition is met. Use for:
- Polling operations
- Batch processing
- Retry logic

**Important:** Cannot nest Do While tasks within each other.

**Required Parameters:**
- \`_loopCondition\`: Expression evaluated each iteration
- \`_loopOver\`: Array of tasks to repeat

**Example:**
\`\`\`json
{
  "_type": "DO_WHILE",
  "_name": "poll_until_complete",
  "_loopCondition": "$.check_status.iteration <= 10 && $.check_status._output.status != 'COMPLETED'",
  "_loopOver": [
    {
      "_type": "REST_CONNECTOR",
      "_name": "check_status",
      "_inputParams": {
        "_url": "https://api.example.com/status",
        "_method": "GET"
      }
    },
    {
      "_type": "SCRIPT_EXECUTION",
      "_name": "wait",
      "_inputParams": {
        "_userType": "utils",
        "_scriptName": "sleep"
      }
    }
  ]
}
\`\`\`

**Integration Pattern: Polling Consumer**
Use Do While to poll an endpoint until data is available or status changes.
    `.trim(),
  },
];

// ============================================================================
// INTEGRATION PATTERNS DOCUMENTATION
// ============================================================================

export const INTEGRATION_PATTERNS = {
  splitter: {
    title: 'Splitter Pattern',
    description: 'Break a large message into smaller parts for individual processing',
    icon: 'fa-th-large',
    example: `
**Splitter Pattern Implementation**

Use Fork Join to process array elements in parallel:

\`\`\`json
{
  "workflowDef": {
    "_name": "batch_splitter",
    "_steps": [
      {
        "_type": "SCRIPT_EXECUTION",
        "_name": "split_batch",
        "_inputParams": {
          "_scriptName": "splitIntoChunks"
        }
      },
      {
        "_type": "FORK_JOIN",
        "_name": "process_parallel",
        "_forkTasks": [
          [
            {"_type": "SCRIPT_EXECUTION", "_name": "process_chunk_1"}
          ],
          [
            {"_type": "SCRIPT_EXECUTION", "_name": "process_chunk_2"}
          ]
        ]
      }
    ]
  }
}
\`\`\`
    `.trim(),
  },
  aggregator: {
    title: 'Aggregator Pattern',
    description: 'Combine results from multiple parallel operations',
    icon: 'fa-compress',
    example: `
**Aggregator Pattern Implementation**

Use Fork Join with a final aggregation step:

\`\`\`json
{
  "workflowDef": {
    "_name": "data_aggregator",
    "_steps": [
      {
        "_type": "FORK_JOIN",
        "_name": "fetch_from_sources",
        "_forkTasks": [
          [
            {"_type": "REST_CONNECTOR", "_name": "source_1"}
          ],
          [
            {"_type": "REST_CONNECTOR", "_name": "source_2"}
          ],
          [
            {"_type": "REST_CONNECTOR", "_name": "source_3"}
          ]
        ]
      },
      {
        "_type": "SCRIPT_EXECUTION",
        "_name": "aggregate_results",
        "_inputParams": {
          "_scriptName": "combineData",
          "_userType": "aggregator"
        }
      }
    ]
  }
}
\`\`\`

**Script Example (aggregate_results):**
\`\`\`javascript
export const run = async (input, ctx) => {
  const source1Data = input.source_1._output;
  const source2Data = input.source_2._output;
  const source3Data = input.source_3._output;

  return {
    combined: [...source1Data, ...source2Data, ...source3Data],
    totalRecords: source1Data.length + source2Data.length + source3Data.length
  };
};
\`\`\`
    `.trim(),
  },
  router: {
    title: 'Content-Based Router',
    description: 'Route messages to different destinations based on content',
    icon: 'fa-directions',
    example: `
**Content-Based Router Implementation**

Use Switch task to route based on data:

\`\`\`json
{
  "workflowDef": {
    "_name": "order_router",
    "_steps": [
      {
        "_type": "SWITCH",
        "_name": "route_by_priority",
        "_switchCaseValue": "\${input.orderPriority}",
        "_decisionCases": {
          "urgent": [
            {"_type": "REST_CONNECTOR", "_name": "urgent_processing"}
          ],
          "normal": [
            {"_type": "REST_CONNECTOR", "_name": "normal_processing"}
          ],
          "bulk": [
            {"_type": "REST_CONNECTOR", "_name": "batch_processing"}
          ]
        },
        "_defaultCase": [
          {"_type": "REST_CONNECTOR", "_name": "default_processing"}
        ]
      }
    ]
  }
}
\`\`\`
    `.trim(),
  },
  enricher: {
    title: 'Content Enricher',
    description: 'Add additional data to a message from external sources',
    icon: 'fa-plus-circle',
    example: `
**Content Enricher Implementation**

Fetch additional data and merge it:

\`\`\`json
{
  "workflowDef": {
    "_name": "data_enricher",
    "_steps": [
      {
        "_type": "REST_CONNECTOR",
        "_name": "fetch_base_data",
        "_inputParams": {
          "_url": "https://api.example.com/orders/\${input.orderId}"
        }
      },
      {
        "_type": "FORK_JOIN",
        "_name": "enrich_data",
        "_forkTasks": [
          [
            {"_type": "REST_CONNECTOR", "_name": "fetch_customer"}
          ],
          [
            {"_type": "REST_CONNECTOR", "_name": "fetch_inventory"}
          ],
          [
            {"_type": "REST_CONNECTOR", "_name": "fetch_pricing"}
          ]
        ]
      },
      {
        "_type": "SCRIPT_EXECUTION",
        "_name": "merge_enriched_data",
        "_inputParams": {
          "_scriptName": "mergeOrderData",
          "_userType": "enricher"
        }
      }
    ]
  }
}
\`\`\`
    `.trim(),
  },
  filter: {
    title: 'Message Filter',
    description: 'Filter messages based on criteria',
    icon: 'fa-filter',
    example: `
**Message Filter Implementation**

Use Switch with conditional logic:

\`\`\`json
{
  "workflowDef": {
    "_name": "message_filter",
    "_steps": [
      {
        "_type": "SCRIPT_EXECUTION",
        "_name": "evaluate_filter",
        "_inputParams": {
          "_scriptName": "checkCriteria",
          "_userType": "filter"
        }
      },
      {
        "_type": "SWITCH",
        "_name": "filter_decision",
        "_switchCaseValue": "\${evaluate_filter._output.shouldProcess}",
        "_decisionCases": {
          "true": [
            {"_type": "SCRIPT_EXECUTION", "_name": "process_message"}
          ]
        },
        "_defaultCase": []
      }
    ]
  }
}
\`\`\`
    `.trim(),
  },
  orchestration: {
    title: 'Process Orchestration',
    description: 'Coordinate multiple services in a business process',
    icon: 'fa-sitemap',
    example: `
**Process Orchestration Example: Order Fulfillment**

\`\`\`json
{
  "workflowDef": {
    "_name": "order_fulfillment",
    "_description": "Complete order processing workflow",
    "_steps": [
      {
        "_type": "SCRIPT_EXECUTION",
        "_name": "validate_order",
        "_inputParams": {
          "_scriptName": "validateOrder",
          "_userType": "validator"
        }
      },
      {
        "_type": "USER_INPUT",
        "_name": "credit_check_approval",
        "_inputParams": {
          "_sendNotification": "yes",
          "_userInput": {
            "approved": {"type": "boolean"}
          }
        }
      },
      {
        "_type": "SWITCH",
        "_name": "check_approval",
        "_switchCaseValue": "\${credit_check_approval._output._userInput.approved}",
        "_decisionCases": {
          "true": [
            {
              "_type": "FORK_JOIN",
              "_name": "process_order",
              "_forkTasks": [
                [
                  {"_type": "REST_CONNECTOR", "_name": "reserve_inventory"},
                  {"_type": "REST_CONNECTOR", "_name": "create_shipment"}
                ],
                [
                  {"_type": "REST_CONNECTOR", "_name": "process_payment"},
                  {"_type": "REST_CONNECTOR", "_name": "send_confirmation"}
                ]
              ]
            }
          ]
        },
        "_defaultCase": [
          {"_type": "SCRIPT_EXECUTION", "_name": "cancel_order"}
        ]
      }
    ]
  }
}
\`\`\`
    `.trim(),
  },
};

// ============================================================================
// DEFAULT FORM VALUES
// ============================================================================

export const getEmptyWorkflowForm = (): WorkflowDefFormData => ({
  name: '',
  description: '',
  userType: 'workflow_def',
  timeoutPolicy: 'TIME_OUT_WF',
  timeoutSeconds: 3600,
  inputParams: '{}',
});

export const getEmptyTaskForm = (): TaskDefFormData => ({
  name: '',
  description: '',
  type: TASK_TYPES.SCRIPT_EXECUTION,
  retryCount: 3,
  retryLogic: 'FIXED',
  retryDelaySeconds: 60,
  timeoutSeconds: 300,
  timeoutPolicy: 'TIME_OUT_WF',
  responseTimeoutSeconds: 180,
  inputParams: '{}',
});

export const getEmptyScheduleForm = (): ScheduleFormData => ({
  name: '',
  cronExpression: '0 0 * * *', // Daily at midnight
  enabled: true,
  inputParams: '{}',
});

// ============================================================================
// CRON EXPRESSION HELPERS
// ============================================================================

export const CRON_PRESETS = [
  { label: 'Every minute', value: '* * * * *' },
  { label: 'Every 5 minutes', value: '*/5 * * * *' },
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Every 30 minutes', value: '*/30 * * * *' },
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Daily at midnight', value: '0 0 * * *' },
  { label: 'Daily at noon', value: '0 12 * * *' },
  { label: 'Weekly (Sunday)', value: '0 0 * * 0' },
  { label: 'Monthly (1st)', value: '0 0 1 * *' },
];

export const parseCronExpression = (cron: string): string => {
  const preset = CRON_PRESETS.find(p => p.value === cron);
  return preset ? preset.label : cron;
};

// ============================================================================
// STATUS COLORS
// ============================================================================

export const STATUS_COLORS: Record<string, string> = {
  RUNNING: '#3b82f6',
  COMPLETED: '#10b981',
  FAILED: '#ef4444',
  PAUSED: '#f59e0b',
  TERMINATED: '#6b7280',
  TIMED_OUT: '#dc2626',
};
